"use client";

import { RotateCcw } from "lucide-react";

import { useEstimator } from "../../_context/EstimatorContext";
import { useUndoableEdit } from "../../_hooks/useUndoableEdit";
import { FetchPill } from "@/fetch-ui/FetchPill";

import { CABIN_LABELS } from "../../_logic/constants";
import type { Origin } from "../../_logic/types";
import { cityKey, groupName, originKey } from "../../_logic/workspace";
import { useRemovePlace } from "../../_hooks/useRemovePlace";
import { AirportPicker } from "./AirportPicker";
import { InlineNumberEdit } from "./InlineNumberEdit";
import { RemovePlaceButton } from "./RemovePlaceButton";

interface OriginLabelProps {
  origin: Origin;
  /** Airport the live fares came from, when known. */
  pricedAirport: string | null;
}

/**
 * Row label for one home city's flights: the city, its traveler count and its airport, the
 * count editable in place and the airport picked from real airports, both with an undo toast,
 * and an × that removes the home city.
 * @param props - The home city and the airport its fares were priced from.
 * @returns The label.
 * @throws Never.
 */
export const OriginLabel = ({ origin, pricedAirport }: OriginLabelProps) => {
  // Hooks
  const { workspace } = useEstimator();
  const apply = useUndoableEdit();
  const { removeOrigin } = useRemovePlace();

  // Derived
  const picked = workspace.airports[cityKey(origin.city)] ?? null;
  const airport = picked ?? pricedAirport;
  const name = origin.group ? `${origin.group} · ${origin.city}` : origin.city;

  // Render
  return (
    <span className="group/place flex min-w-0 flex-col gap-0.5">
      <span className="flex min-w-0 flex-wrap items-center gap-x-1 gap-y-0.5 text-sm font-medium text-gray-900">
        {/* The row is the home city's flights, so the city alone says it; one line, full name on hover. */}
        <span className="min-w-0 truncate" title={name}>
          {name}
        </span>
        {origin.cabin && origin.cabin !== workspace.cabin && (
          <FetchPill tone="indigo" size="sm" className="shrink-0">
            {CABIN_LABELS[origin.cabin]}
          </FetchPill>
        )}
        <RemovePlaceButton
          name={origin.group ? `${origin.group} (${origin.city})` : origin.city}
          onRemove={() => removeOrigin(origin)}
        />
      </span>
      <span className="flex flex-nowrap items-center gap-x-1.5 whitespace-nowrap text-xs font-medium text-gray-500">
        <InlineNumberEdit
          display={`${origin.count.toLocaleString("en-US")} ${origin.count === 1 ? "person" : "people"}`}
          initial={String(origin.count)}
          label={`People flying from ${origin.city}`}
          field={{ widthClass: "w-10", inputMode: "numeric" }}
          onCommit={(text) => {
            const count = Number.parseInt(text, 10);
            if (!Number.isInteger(count) || count < 1) return false;
            apply({ type: "setOriginCount", key: originKey(origin), count }, `${groupName(origin)}: ${count} people`);
          }}
        />
        <span aria-hidden>·</span>
        <AirportPicker
          city={origin.city}
          code={airport}
          picked={picked !== null}
          onPick={(code) => apply({ type: "setAirport", city: origin.city, code }, `${origin.city} flies from ${code}`)}
        />
        {picked && (
          // ui-lint-disable-next-line raw-control: a custom control (segment, chip, calendar day, stepper, icon or inline editor) styled to its row; FetchButton's sizes and 3D treatment don't fit
          <button
            type="button"
            aria-label={`Use the default airport for ${origin.city}`}
            onClick={() =>
              apply({ type: "setAirport", city: origin.city, code: null }, `${origin.city} back to its main airport`)
            }
            className="relative after:absolute after:-inset-3 after:content-[''] rounded p-0.5 text-gray-500 hover:bg-gray-100 hover:text-gray-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
          >
            <RotateCcw className="size-3" />
          </button>
        )}
      </span>
    </span>
  );
};
